import React from 'react';

import { Link } from 'react-router-dom';

import { useTranslation } from 'react-i18next';

import styled from 'styled-components';

import { DashboardSection } from './DashboardSection';

import { Heading } from '../../design-system/components/Typography';

import { spacing } from '../../design-system/tokens/spacing';

import { typography } from '../../design-system/tokens/typography';

import { breakpoints } from '../../design-system/tokens/breakpoints';

import { DashboardOverviewView } from '../../types/dashboard';



const Panel = styled(DashboardSection)``;



const PanelTitle = styled(Heading)`

  margin: 0 0 ${spacing[5]} 0 !important;

`;



const MetricsGrid = styled.div`

  display: grid;

  grid-template-columns: repeat(2, 1fr);

  gap: 0;



  @media (max-width: 380px) {

    grid-template-columns: 1fr;

  }



  @media (min-width: ${breakpoints.md}) {

    grid-template-columns: repeat(4, 1fr);

  }

`;



const MetricCell = styled.div<{ $clickable?: boolean }>`

  padding: ${spacing[2]} ${spacing[5]};

  border-right: 1px solid ${props => props.theme.colors.border};

  border-bottom: 1px solid ${props => props.theme.colors.border};



  @media (min-width: ${breakpoints.md}) {

    border-bottom: none;

    &:last-child {

      border-right: none;

    }

  }



  @media (max-width: ${breakpoints.md}) {

    &:nth-child(2n) {

      border-right: none;

    }

    &:nth-last-child(-n + 2) {

      border-bottom: none;

    }

  }



  @media (max-width: 380px) {

    border-right: none;

    &:last-child {

      border-bottom: none;

    }

  }



  ${props =>

    props.$clickable &&

    `

    transition: background 150ms ease;

    &:hover {

      background: ${props.theme.colors.surfaceHover};

    }

  `}

`;



const MetricLink = styled(Link)`

  display: block;

  text-decoration: none;

  color: inherit;

  padding: ${spacing[2]} ${spacing[5]};

  margin: -${spacing[2]} -${spacing[5]};

  &:focus-visible {

    outline: 2px solid ${props => props.theme.colors.primary};

    outline-offset: -2px;

  }

`;



const MetricLabel = styled.p`

  font-size: ${typography.fontSize.sm};

  font-weight: ${typography.fontWeight.medium};

  color: ${props => props.theme.colors.textSecondary};

  margin: 0 0 ${spacing[1]} 0;

`;



const MetricValue = styled.p`

  font-size: ${typography.fontSize.metric};

  font-weight: ${typography.fontWeight.semibold};

  line-height: ${typography.lineHeight.tight};

  margin: 0;

  color: ${props => props.theme.colors.text};

`;



const MetricSupporting = styled.p`

  font-size: ${typography.fontSize.sm};

  color: ${props => props.theme.colors.textMuted};

  margin: ${spacing[1]} 0 0 0;

  line-height: ${typography.lineHeight.normal};

`;



interface OverviewMetricProps {

  label: string;

  value: string | number;

  supporting?: string | null;

  href?: string;

}



const OverviewMetric: React.FC<OverviewMetricProps> = ({ label, value, supporting, href }) => {

  const inner = (

    <>

      <MetricLabel>{label}</MetricLabel>

      <MetricValue>{value}</MetricValue>

      {supporting && <MetricSupporting>{supporting}</MetricSupporting>}

    </>

  );



  if (href) {

    return (

      <MetricCell $clickable>

        <MetricLink to={href}>{inner}</MetricLink>

      </MetricCell>

    );

  }



  return <MetricCell>{inner}</MetricCell>;

};



interface OverviewPanelProps {

  overview: DashboardOverviewView;

}



export const OverviewPanel: React.FC<OverviewPanelProps> = ({ overview }) => {

  const { t } = useTranslation();



  return (

    <Panel>

      <PanelTitle variant="h3" as="h2" style={{ fontSize: typography.fontSize.section }}>

        {t('dashboard.overview.title')}

      </PanelTitle>

      <MetricsGrid>

        <OverviewMetric

          label={t('dashboard.overview.documents')}

          value={overview.documentsCount}

          supporting={overview.documentsSupporting}

          href="/documents"

        />

        <OverviewMetric

          label={t('dashboard.overview.openRequests')}

          value={overview.openRequestsCount}

          supporting={overview.totalRequestsLabel}

          href="/change-requests"

        />

        <OverviewMetric

          label={t('dashboard.overview.profileCompletion')}

          value={`${overview.profilePercentage}%`}

          supporting={overview.missingFieldsSupporting}

          href="/profile/edit"

        />

        <OverviewMetric

          label={t('dashboard.overview.certificateStatus')}

          value={overview.vignetteStatusLabel}

          supporting={overview.vignetteSupporting}

          href={overview.vignetteSupporting ? '/profile' : undefined}

        />

      </MetricsGrid>

    </Panel>

  );

};



export default OverviewPanel;


